/**
 * Canonical section registry for Claim Editor navigation, status, and error grouping.
 */

export type ClaimSectionId =
    | 'patient'
    | 'guarantor'
    | 'subscriber'
    | 'services'
    | 'billingProvider'
    | 'facility'
    | 'additionalProviders'
    | 'additionalInformation'
    | 'settings';

export type ClaimSectionIcon =
    | 'person'
    | 'wallet'
    | 'badge'
    | 'medical'
    | 'domain'
    | 'apartment'
    | 'groups'
    | 'description'
    | 'tune';

export interface ClaimEditorSectionDef {
    id: ClaimSectionId;
    title: string;
    navLabel: string;
    /** Icon key mapped to @mdi/js in the UI layer */
    icon: ClaimSectionIcon;
    /** Matches ClaimEditor `disabledSections` keys when present */
    disabledKey?: string;
}

export const CLAIM_EDITOR_SECTIONS: ClaimEditorSectionDef[] = [
    { id: 'patient', title: 'Patient Information', navLabel: 'Patient', icon: 'person', disabledKey: 'patient' },
    {
        id: 'guarantor',
        title: 'Guarantor Information',
        navLabel: 'Guarantor',
        icon: 'wallet',
        disabledKey: 'guarantor',
    },
    {
        id: 'subscriber',
        title: 'Subscriber Information',
        navLabel: 'Subscriber',
        icon: 'badge',
        disabledKey: 'subscriber',
    },
    { id: 'services', title: 'Services', navLabel: 'Services', icon: 'medical' },
    {
        id: 'billingProvider',
        title: 'Billing Provider Information',
        navLabel: 'Billing Provider',
        icon: 'domain',
        disabledKey: 'provider',
    },
    { id: 'facility', title: 'Facility Information', navLabel: 'Facility', icon: 'apartment', disabledKey: 'facility' },
    {
        id: 'additionalProviders',
        title: 'Additional Providers',
        navLabel: 'Providers',
        icon: 'groups',
        disabledKey: 'additionalProviders',
    },
    {
        id: 'additionalInformation',
        title: 'Additional Information',
        navLabel: 'Additional Info',
        icon: 'description',
        disabledKey: 'additionalInformation',
    },
    { id: 'settings', title: 'Settings', navLabel: 'Settings', icon: 'tune', disabledKey: 'settings' },
];

export function sectionDomId(sectionId: ClaimSectionId): string {
    return `claim-section-${sectionId}`;
}

export function fieldDomId(fieldKey: string): string {
    return `claim-field-${fieldKey.replace(/[^a-zA-Z0-9]+/g, '-')}`;
}

/**
 * Additional Information sits at the very end of the page, so jumping straight to its top can
 * drag the viewport past the end of a short page. Land at the end of Additional Providers
 * instead, offset by the REAL measured height of the trailing sections (not a guessed pixel
 * constant — that varies by screen and by how much of Settings is expanded).
 *
 * Settings itself is the very last section, and no anchored offset landed reliably for it — its
 * own scroll target is redirected to Additional Information below, so Settings ends up peeking
 * in underneath instead of being jumped to directly.
 */
const SCROLL_ANCHOR_OVERRIDES: Partial<Record<ClaimSectionId, ClaimSectionId>> = {
    additionalInformation: 'additionalProviders',
};

const TRAILING_SECTIONS: ClaimSectionId[] = ['additionalInformation', 'settings'];

function findScrollParent(el: HTMLElement): HTMLElement | null {
    let node = el.parentElement;
    while (node) {
        const overflowY = getComputedStyle(node).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
}

function measuredHeight(id: ClaimSectionId): number {
    const el = document.getElementById(sectionDomId(id));
    return el ? el.getBoundingClientRect().height : 0;
}

export function scrollToSection(sectionId: ClaimSectionId): void {
    const effectiveId = sectionId === 'settings' ? 'additionalInformation' : sectionId;

    const anchorId = SCROLL_ANCHOR_OVERRIDES[effectiveId];
    const anchorEl = anchorId ? document.getElementById(sectionDomId(anchorId)) : null;

    if (anchorEl) {
        const scrollParent = findScrollParent(anchorEl);
        const viewportHeight = scrollParent?.clientHeight ?? window.innerHeight;
        const trailingHeight = TRAILING_SECTIONS.reduce((sum, id) => sum + measuredHeight(id), 0);

        // Only take the short anchored jump when the real trailing content wouldn't fill the
        // screen anyway. Otherwise there's no overscroll risk, so fall through to a normal jump.
        if (trailingHeight < viewportHeight) {
            const delta = anchorEl.getBoundingClientRect().bottom - viewportHeight + trailingHeight;
            if (scrollParent) {
                scrollParent.scrollBy({ top: delta, behavior: 'smooth' });
            } else {
                window.scrollBy({ top: delta, behavior: 'smooth' });
            }
            return;
        }
    }

    const el = document.getElementById(sectionDomId(effectiveId));
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function scrollToField(fieldKey: string, sectionId?: ClaimSectionId): void {
    if (sectionId) {
        scrollToSection(sectionId);
    }
    requestAnimationFrame(() => {
        const el = document.getElementById(fieldDomId(fieldKey));
        if (!el) {
            if (sectionId) scrollToSection(sectionId);
            return;
        }
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable =
            el instanceof HTMLInputElement ||
            el instanceof HTMLSelectElement ||
            el instanceof HTMLTextAreaElement ||
            el instanceof HTMLButtonElement
                ? el
                : el.querySelector<HTMLElement>('input, select, textarea, button, [tabindex]');
        focusable?.focus?.({ preventScroll: true });
    });
}
